import { Drawer, Box, Typography, Button, useMediaQuery, Modal, Paper } from "@mui/material";
import { product_brand, product_category, product_child_tag, product_color, product_material, product_structure, real_estate } from "@prisma/client";
import { useTranslation } from "next-i18next";
import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from "react";
import { DownloadIcon, CartIconWhite, FrontEndBlackButton, FrontEndWhiteButtonWithBorder, SmallClose2Icon, getPriceCombo, getProductAttributeDisplay, useMySnackbar, PureIconButton } from "../Common";
import { BrochureAndDocumentType, ProductTagType, ProductType, SimpleNameData } from "../cms_common/CMS_Common";
import { fetchWrapper } from "@/com_lib/request";
import { changeExtension, getLanguageText, get_productImage, splitFileNames } from "@/com_lib/common";

// Import Swiper React components
import { Swiper, SwiperSlide } from "swiper/react";

// Import Swiper styles
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/scrollbar";
import "swiper/css/pagination";

// import required modules
import { Scrollbar, Navigation } from "swiper";
import classNames from "classnames";
import { addProduct } from "@/com_lib/signal/CartSignal";
import { useLightBox } from "../misc/Lightbox";
import { useEffectEvent } from "@/com_lib/hooks";
import theme from "@/com_lib/theme";
import ModelViewer, { getMobileARPath } from "./ModelViewer";

const emptyArray = [] as any;

export function useGetProductDetail(
	realEstate: real_estate, set_open_loadingScreen: Dispatch<SetStateAction<boolean>>,
	canAddToCart = false,
	withProductData = false,
	_brands: SimpleNameData[] = emptyArray, _categories: SimpleNameData[] = emptyArray, _tags: product_child_tag[] = emptyArray,
	_structures: SimpleNameData[] = emptyArray, _materials: SimpleNameData[] = emptyArray, _colors: SimpleNameData[] = emptyArray,
) {
	const { t, i18n } = useTranslation();

	const [productId, set_productId] = useState(0);
	const [productData, set_productData] = useState<ProductType>(null);
	const [selectedProductImage, set_selectedProductImage] = useState<string>(null);

	const [categories, setCategories] = useState<SimpleNameData[]>(emptyArray);
	const [brands, setBrands] = useState<SimpleNameData[]>(emptyArray);
	//const [motherTags, set_motherTags] = useState<product_tag[]>(emptyArray);
	const [tags, setTags] = useState<product_child_tag[]>(emptyArray);
	const [structures, set_structures] = useState<SimpleNameData[]>(emptyArray);
	const [materials, set_materials] = useState<SimpleNameData[]>(emptyArray);
	const [colors, set_colors] = useState<SimpleNameData[]>(emptyArray);
	useEffect(() => {
		setBrands(_brands);
		setCategories(_categories);
		setTags(_tags);
		set_structures(_structures);
		set_materials(_materials);
		set_colors(_colors);
	}, [
		_brands, _categories, _tags,
		_structures, _materials, _colors
	]);

	const product_images = useMemo(() => {
		return splitFileNames(productData?.file_product_images ?? "");
	}, [productData]);

	const mainApi = "/api/admin_product/product";

	const getData = useEffectEvent((productId: number) => {
		if (productId && !withProductData) {
			// copy from EditProduct.tsx
			const fetchThreeOptions = async () => {
				try {
					const body = {
						action: "getSixOptions",
						real_estate_id: realEstate.id
					};
					const [brands, categories, tags, structures, materials, colors] = (await fetchWrapper.post(
						mainApi,
						body
					)) as [product_brand[], product_category[], ProductTagType[], product_structure[], product_material[], product_color[]];
					setBrands(brands);
					setCategories(categories);
					//set_motherTags(tags);
					setTags(tags.selectMany(t => t.product_child_tag).toArray());
					set_structures(structures);
					set_materials(materials);
					set_colors(colors);
				} catch (error) {
					console.error(
						`Error fetching data for action getSixOptions":`,
						error
					);
				}
			};
			// copy from EditProduct.tsx
			const getProduct = async () => {
				try {
					let body = {
						action: "getProduct",
						id: productId,
					};

					const product = (await fetchWrapper.post(
						mainApi,
						body
					));
					set_productData(product);
				} catch (error) {
					console.error("Error fetching brand data:", error);
				}
			};

			set_productData(null);
			// Let the product clear first, then load the new product.
			requestAnimationFrame(() => {
				set_open_loadingScreen(true);
				Promise.all([fetchThreeOptions(), getProduct()]).then(() => {
					set_open_loadingScreen(false);
				});
			});
		}
	});
	useEffect(() => {
		getData(productId);
	}, [productId]);
	useEffect(() => {
		set_selectedProductImage(null);
	}, [productId, productData]);

	const SlideSize = "60px";

	const [showAdditionalInfo, set_showAdditionalInfo] = useState(false);

	const largeImage = Boolean(productData) ? (
		selectedProductImage ?
			get_productImage(realEstate, productData?.id, selectedProductImage)
			:
			get_productImage(realEstate, productData?.id, productData.file_thumbnail_image)
	) : null;

	const [lightBoxEle, set_lightboxOpen] = useLightBox(largeImage);

	const [addedToCartSnackbar, set_open_addedToCartSnackbar] = useMySnackbar(t("已經加入購物車"));

	const [arFilePath, set_arFilePath] = useState<string>(null);
	const [openARViewer, set_openARViewer] = useState(false);
	const isMd = useMediaQuery(theme.breakpoints.up("md"));
	const showARViewer = useCallback((_arFilePath: string) => {
		if (isMd) {
			set_arFilePath(_arFilePath);
			set_openARViewer(true);
		}
		else {
			const mobileARPath = getMobileARPath(_arFilePath);
			window.open(mobileARPath, "_blank");
		}
	}, [isMd]);
	const modelViewer = openARViewer ? <Modal
		open={openARViewer} sx={{zIndex: 1500, display: "flex", justifyContent: "center", alignItems: "center"}}
	>
		<Paper sx={{
			display: "flex", maxHeight: "100dvh", position: "relative"
		}}>
			<ModelViewer modelPath={arFilePath} set_open_loadingScreen={set_open_loadingScreen} />
			<Button
				sx={{ ...PureIconButton, position: "absolute", top: "10px", right: "10px" }}
				onClick={() => set_openARViewer(false)}
			>
				{iconClose}
			</Button>
		</Paper>
	</Modal> : null;

	const productDetailElement = Boolean(productData) && <>
		<Box sx={{ position: "relative", display: "flex" }}>
			<Box component="img"
				src={largeImage}
			// sx={{ cursor: "pointer" }}
			// onClick={() => set_lightboxOpen(true)}
			/>
			{productData.file_3dmodel &&
				<Box
					sx={{
						position: "absolute", bottom: "10px", right: "10px",
						display: "flex", padding: "6px 8px", justifyContent: "center", alignItems: "center", gap: "4px",
						borderRadius: "5px", background: "#E5E5E5", cursor: "pointer"
					}}
					onClick={() => {
						const _arFilePath = get_productImage(realEstate, productData?.id, productData.file_3dmodel);
						showARViewer(_arFilePath);
					}}
				>
					<svg xmlns="http://www.w3.org/2000/svg" width="21" height="20" viewBox="0 0 21 20" fill="none">
						<path fillRule="evenodd" clipRule="evenodd" d="M7.34464 18.9376L3.27321 16.3811C2.159 15.6823 1.60121 15.3318 1.30129 14.8191C1 14.3063 1 13.703 1 12.501V7.87098C1 7.48703 1 7.16509 1.0095 6.88726L9.14286 11.9907V20C8.65836 19.7615 8.09379 19.4074 7.34464 18.9376ZM11.8571 19.9988C12.3416 19.7603 12.9062 19.405 13.6554 18.9352L17.7268 16.3799C18.841 15.6812 19.3988 15.3306 19.6987 14.8179C20 14.3051 20 13.703 20 12.4999V7.86979C20 7.48584 20 7.16389 19.9905 6.88607L11.8571 11.9907V19.9988ZM18.8166 4.69206L10.5 9.91236L2.18343 4.69326C2.47114 4.49294 2.82807 4.26876 3.27321 3.98975L7.34464 1.43445C8.86871 0.478149 9.63143 0 10.5 0C11.3686 0 12.1313 0.476957 13.6554 1.43445L17.7268 3.98975C18.1733 4.26876 18.5289 4.49174 18.8166 4.69206Z" fill="#231916" />
					</svg>
					<Typography>
						{t("3D")}
					</Typography>
				</Box>
			}
		</Box>
		{product_images.length > 0 &&
			<Box
				sx={{
					px: "12px", py: "20px",
					".swiper": {
						height: `calc(${SlideSize} + 16px)`,
					},
					".swiper-slide": {
						overflow: "hidden",
						width: SlideSize, height: SlideSize,
						border: "1px solid transparent",
						borderRadius: "5px", cursor: "pointer",
						"&.selected": {
							borderColor: "#231916",
						},
						"img": {
							width: "100%", height: "100%", objectFit: "cover",
							borderRadius: "5px"
						}
					},
					".swiper-button-prev, .swiper-button-next": {
						display: "none"
					},
				}}
			>
				<Swiper
					slidesPerView="auto"
					spaceBetween={12}
					scrollbar={{
						hide: false,
					}}
					navigation={true}
					modules={[Scrollbar, Navigation]}
				>
					{
						product_images.map(image => {
							return <SwiperSlide
								key={image} className={classNames({ "selected": selectedProductImage == image })}
								onClick={() => {
									if (selectedProductImage == image)
										set_selectedProductImage(null); // toggle effect.
									else
										set_selectedProductImage(image);
								}}
							>
								<img src={get_productImage(realEstate, productData?.id, image)} />
							</SwiperSlide>
						})
					}
				</Swiper>
			</Box>
		}
		<Box sx={{ px: "24px", py: "40px", display: "flex", flexDirection: "column", gap: 1 }}>
			<Typography sx={{ fontSize: "18px", fontWeight: 500 }}>{getLanguageText(productData, "name", i18n.language)}</Typography>
			<Typography sx={{ fontSize: "14px", color: "#6F6F6F" }}>{t("產品編號")}: {productData.code}</Typography>
			<Typography sx={{ fontSize: "14px", whiteSpace: "pre-wrap" }}>{getLanguageText(productData, "description", i18n.language)}</Typography>
			{getProductAttributeDisplay(
				t, i18n.language, productData,
				brands, categories, tags, structures, materials, colors
			)}
			{getPriceCombo(productData.price, productData.discount_price)}
			{canAddToCart &&
				<Button
					sx={{ ...FrontEndBlackButton, mt: 2, mb: 3 }}
					type="submit"
					fullWidth
					startIcon={CartIconWhite}
					onClick={() => {
						addProduct(productData.id, 1);
						set_open_addedToCartSnackbar(true);
					}}
				>
					{t("加到購物車")}
				</Button>
			}
			{productData.file_brochure_and_document?.length > 0 && <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
				<Box
					sx={{ height: "40px", display: "flex", alignItems: "center", gap: 1, p: 1, cursor: "pointer", backgroundColor: "#F3F3F3" }}
					onClick={() => set_showAdditionalInfo(old => !old)}
				>
					{showAdditionalInfo ?
						<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
							<path d="M19 13H13H12H11.5H11H5V11H11H11.5H12.5H13H19V13Z" fill="#231916" />
						</svg>
						:
						<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
							<path d="M19 13H13V19H11V13H5V11H11V5H13V11H19V13Z" fill="#231916" />
						</svg>
					}
					<Typography>{t("更多產品資料")}</Typography>
				</Box>
				{showAdditionalInfo &&
					<Box
						sx={{
							display: "flex",
							flexWrap: "wrap",
							columnGap: "31px", rowGap: "40px"
						}}
					>
						{productData.file_brochure_and_document.map(
							(file: BrochureAndDocumentType, index: number) => (
								<Box
									key={file.file_pdf}
									sx={{
										width: "calc(50% - 16px)", display: "flex", flexDirection: "column", alignItems: "center", gap: 1
									}}
								>
									<>
										<img
											src={get_productImage(realEstate, productData?.id, changeExtension(file.file_pdf, ".webp"))}
											alt={`Product Image ${index}`}
											style={{ position: "relative", objectFit: "cover" }}
										/>
										<Typography
											sx={{
												fontSize: "11px", color: "#6F6F6F"
											}}
										>
											{file.pdf_name}
										</Typography>
										<Button
											sx={FrontEndWhiteButtonWithBorder}
											startIcon={DownloadIcon}
											onClick={() => {
												const url = get_productImage(realEstate, productData?.id, file.file_pdf);
												// window.open(url, '_blank');
												const link = document.createElement("a");
												link.href = url;
												link.download = `${file.pdf_name}.pdf`;

												document.body.appendChild(link);
												link.click();

												document.body.removeChild(link);
											}}
										>
											{t("下載")}
										</Button>
									</>
								</Box>
							)
						)}
					</Box>
				}
			</Box>
			}
		</Box>
		{lightBoxEle}
		{addedToCartSnackbar}
		{modelViewer}
	</>

	return [productDetailElement, set_productId, set_productData] as const;
}

export const iconClose = <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
	<g clipPath="url(#clip0_247_10063)">
		<path fillRule="evenodd" clipRule="evenodd" d="M12.0117 13.3324L17.3891 18.7098C17.5683 18.883 17.8085 18.9788 18.0577 18.9766C18.3069 18.9744 18.5453 18.8745 18.7216 18.6982C18.8978 18.522 18.9978 18.2836 19 18.0343C19.0021 17.7851 18.9063 17.545 18.7332 17.3657L13.3558 11.9883L18.7332 6.61094C18.9063 6.43166 19.0021 6.19155 19 5.94231C18.9978 5.69307 18.8978 5.45466 18.7216 5.27842C18.5453 5.10217 18.3069 5.0022 18.0577 5.00004C17.8085 4.99787 17.5683 5.09368 17.3891 5.26684L12.0117 10.6442L6.6343 5.26684C6.45422 5.09796 6.2155 5.00578 5.96865 5.00979C5.7218 5.01379 5.4862 5.11368 5.31169 5.28831C5.13718 5.46295 5.03746 5.69862 5.03363 5.94547C5.02979 6.19232 5.12215 6.43098 5.29115 6.61094L10.6676 11.9883L5.2902 17.3657C5.19941 17.4534 5.12699 17.5583 5.07717 17.6742C5.02736 17.7902 5.00113 17.915 5.00004 18.0412C4.99894 18.1674 5.02299 18.2926 5.07079 18.4094C5.11858 18.5262 5.18916 18.6323 5.27842 18.7216C5.36767 18.8108 5.4738 18.8814 5.59062 18.9292C5.70744 18.977 5.83261 19.0011 5.95883 19C6.08505 18.9989 6.20978 18.9726 6.32575 18.9228C6.44173 18.873 6.54662 18.8006 6.6343 18.7098L12.0117 13.3324Z" fill="black" stroke="black" strokeWidth="0.5" />
	</g>
	<defs>
		<clipPath id="clip0_247_10063">
			<rect width="24" height="24" fill="white" />
		</clipPath>
	</defs>
</svg>


export function useProductDetailDrawer(
	realEstate: real_estate, set_open_loadingScreen: Dispatch<SetStateAction<boolean>>,
	canAddToCart = false,
	withProductData = false,
	_brands: SimpleNameData[] = emptyArray, _categories: SimpleNameData[] = emptyArray, _tags: product_child_tag[] = emptyArray,
	_structures: SimpleNameData[] = emptyArray, _materials: SimpleNameData[] = emptyArray, _colors: SimpleNameData[] = emptyArray,
) {
	const [open, set_open] = useState(false);

	const width = "375px";

	const [productDetailElement, set_productId, set_productData] = useGetProductDetail(
		realEstate, set_open_loadingScreen,
		canAddToCart,
		withProductData,
		_brands, _categories, _tags,
		_structures, _materials, _colors
	);

	// Need set the draw's zindex higher.
	const drawer = (
		<Drawer className="ProductDetailDrawer" sx={{ zIndex: 1400 }} open={open} onClose={() => set_open(false)} anchor="right">
			<Box sx={{ height: "100%", overflowY: "auto", display: "flex" }}>
				<Box
					sx={{
						height: "100%", width: width, display: "flex", flexDirection: "column", position: "relative",
						"img": {
							width: "100%", height: "auto"
						},
						".title": {
							fontWeight: "500", fontSize: "14px", mb: 1
						},
						".desc": {
							fontSize: "14px", mb: 3
						}
					}}
				>
					{productDetailElement}
					<Box
						sx={{
							position: "absolute", right: "10px", top: "10px", width: "32px", height: "32px",
							display: "flex", justifyContent: "center", alignItems: "center",
							cursor: "pointer"
						}}
						onClick={() => set_open(false)}
					>
						{SmallClose2Icon}
					</Box>
				</Box>
			</Box>
		</Drawer>
	);

	// The caller should either use 
	// set_productId(), which need to retrive detail from server
	// or 
	// set_productData(), which don't need to retrive data from server
	return [drawer, set_open, set_productId, set_productData] as const;
}